import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, ShieldCheck, Sparkles } from 'lucide-react';
import { friendlyAuthError } from '../lib/errors';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 0.9, 0.32, 1] },
  }),
};

export default function PortalLoginShell({
  title,
  subtitle,
  badge,
  expectedRole,
  onSuccessPath,
  iconBg,
  Icon,
  footerLink,
  emailPlaceholder = 'you@example.com',
  testidPrefix = 'login',
}) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // If the user is already signed in with the correct role, skip the login
  // form and jump straight to the dashboard. Prevents the "portal locks up
  // on browser back" issue where the login route showed the form again even
  // though the session cookie was still valid.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/me`,
          { credentials: 'include' }
        );
        if (!res.ok || cancelled) return;
        const me = await res.json();
        if (!expectedRole) return;
        const allowed = Array.isArray(expectedRole) ? expectedRole : [expectedRole];
        if (allowed.includes(me.role)) {
          navigate(onSuccessPath, { replace: true });
        }
      } catch (_) { /* offline / unauthenticated — stay on the form */ }
    })();
    return () => { cancelled = true; };
  }, [expectedRole, onSuccessPath, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email, password }),
        }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Login failed');
      if (expectedRole) {
        const allowed = Array.isArray(expectedRole) ? expectedRole : [expectedRole];
        if (!allowed.includes(data.role)) {
          throw new Error(`Access denied. ${badge} credentials required.`);
        }
      }
      // `replace: true` — the login page is popped from history so that
      // hitting browser Back from the dashboard goes to whatever the user
      // was viewing before signing in (marketing site) instead of showing
      // the login form again.
      navigate(onSuccessPath, { replace: true });
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-tge-paper">
      <main className="flex-grow flex items-center justify-center px-4 pt-[100px] pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 0.9, 0.32, 1] }}
          className="w-full max-w-5xl bg-white rounded-3xl shadow-[0_20px_60px_rgba(27,42,107,0.12)] overflow-hidden grid lg:grid-cols-[1.05fr_1fr]"
        >
          {/* Left brand panel */}
          <div className="relative bg-tge-navy text-white p-10 lg:p-12 hidden lg:flex flex-col justify-between overflow-hidden">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-tge-gold/15 blur-3xl" />
            <div className="absolute -bottom-24 -left-12 w-72 h-72 rounded-full bg-indigo-400/10 blur-3xl" />

            <div className="relative">
              <Link to="/login" className="inline-flex items-center gap-2 text-xs text-tge-cream/80 hover:text-tge-gold transition" data-testid={`${testidPrefix}-back`}>
                <ArrowLeft size={14} /> All portals
              </Link>

              <motion.div custom={1} initial="hidden" animate="show" variants={fadeUp} className={`w-14 h-14 rounded-2xl ${iconBg} flex items-center justify-center mt-12 shadow-lg`}>
                <Icon className="text-white" size={26} strokeWidth={1.8} />
              </motion.div>

              <motion.p custom={2} initial="hidden" animate="show" variants={fadeUp} className="uppercase tracking-[0.18em] text-[10px] font-semibold text-tge-gold mt-7">
                {badge}
              </motion.p>
              <motion.h1 custom={3} initial="hidden" animate="show" variants={fadeUp} className="font-serif-display text-4xl xl:text-5xl text-white leading-[1.1] mt-3">
                {title}
              </motion.h1>
              <motion.p custom={4} initial="hidden" animate="show" variants={fadeUp} className="text-tge-cream/75 text-sm leading-relaxed mt-4 max-w-sm">
                {subtitle}
              </motion.p>
            </div>

            <motion.div custom={5} initial="hidden" animate="show" variants={fadeUp} className="relative mt-10 grid grid-cols-3 gap-4 pt-6 border-t border-white/10">
              <Stat label="Students" value="500+" />
              <Stat label="Partners" value="50+" />
              <Stat label="Countries" value="12" />
            </motion.div>
          </div>

          {/* Right form panel */}
          <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
            {/* Mobile-only header (since left panel is hidden) */}
            <div className="lg:hidden mb-6">
              <Link to="/login" className="inline-flex items-center gap-2 text-xs text-tge-muted hover:text-tge-navy transition" data-testid={`${testidPrefix}-back-mobile`}>
                <ArrowLeft size={14} /> All portals
              </Link>
              <h1 className="font-serif-display text-3xl text-tge-navy mt-4">{title}</h1>
              <p className="text-tge-muted text-sm mt-2">{subtitle}</p>
            </div>

            <h2 className="hidden lg:block font-serif-display text-2xl text-tge-navy mb-1">Sign in</h2>
            <p className="hidden lg:block text-tge-muted text-sm mb-7">Use your credentials to continue.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl text-sm"
                  data-testid={`${testidPrefix}-error`}
                >
                  {error}
                </motion.div>
              )}

              <div>
                <label className="block text-[12px] font-semibold uppercase tracking-wider text-tge-muted mb-1.5" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={emailPlaceholder}
                  data-testid={`${testidPrefix}-email`}
                  className="w-full h-12 px-4 rounded-xl bg-tge-cream-soft border border-transparent focus:border-tge-gold focus:bg-white focus:outline-none focus:ring-4 focus:ring-tge-gold/15 transition placeholder:text-tge-muted/50 text-[15px]"
                />
              </div>

              <div className="relative">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-tge-muted" htmlFor="password">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-[11px] font-semibold text-tge-navy hover:text-tge-gold transition underline decoration-tge-gold/40 underline-offset-4"
                    data-testid={`${testidPrefix}-forgot-password`}
                  >
                    Forgot password?
                  </Link>
                </div>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid={`${testidPrefix}-password`}
                  className="w-full h-12 px-4 pr-12 rounded-xl bg-tge-cream-soft border border-transparent focus:border-tge-gold focus:bg-white focus:outline-none focus:ring-4 focus:ring-tge-gold/15 transition placeholder:text-tge-muted/50 text-[15px]"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-[42px] text-tge-muted hover:text-tge-navy transition p-1"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  data-testid={`${testidPrefix}-password-toggle`}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid={`${testidPrefix}-submit`}
                className="group w-full h-12 rounded-xl bg-tge-navy hover:bg-tge-navy-deep text-white font-semibold text-[15px] transition disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-tge-navy/20 inline-flex items-center justify-center gap-2"
              >
                {loading ? 'Signing in…' : 'Sign in securely'}
                {!loading && <ShieldCheck size={16} className="opacity-80" />}
              </button>
            </form>

            <div className="flex items-center gap-2 text-[11px] text-tge-muted/70 mt-5">
              <ShieldCheck size={12} className="text-emerald-500" />
              <span>Encrypted connection · Cookies secured</span>
            </div>

            {footerLink && (
              <p className="text-center text-sm text-tge-muted mt-6 pt-6 border-t border-tge-cream">
                {footerLink.label}{' '}
                <Link
                  to={footerLink.path}
                  className="text-tge-navy font-semibold underline decoration-tge-gold/60 underline-offset-4 hover:decoration-tge-gold"
                  data-testid={`${testidPrefix}-footer-link`}
                >
                  {footerLink.cta}
                </Link>
              </p>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-serif-display text-2xl text-tge-gold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-tge-cream/60 mt-0.5">{label}</div>
    </div>
  );
}
